import React from 'react';
import ExternalLink from '~/components/ExternalLink';
import { Navigate } from 'react-router';
import { Button, Card, CardBody, ModalBody, Spinner } from 'reactstrap';
import DialogModal from '~/components/DialogModal';
import WaitFor from '~/components/WaitFor';
import { PaypalCompletionResult, PaypalSuccessRequest } from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';
import { WithSearch } from '~/lib/helper/LocationHelper';
import { AsyncStore } from '~/lib/store/AsyncStore';
import { CancelBlurb } from './CancelBlurb';
import TransactionError from './TransactionError';
import { BillingInvoicesRoute } from '~/lib/helper/RouteHelper';

interface PaypalProcessProps {
    invoiceId?: string;
    transactionId?: string;
    actionId?: string;
    payerId?: string;
}

class PaypalProcessWithParams extends React.Component<PaypalProcessProps> {
    resultStore = new AsyncStore<PaypalCompletionResult, undefined>(
        () =>
            getAppContext().siteApiClient.completePaypalPayment({
                payer_id: this.props.payerId,
                transaction_id: this.props.transactionId,
                action_id: this.props.actionId,
            } as PaypalSuccessRequest),
        undefined
    );

    componentDidMount(): void {
        void this.resultStore.load();
    }

    loadingView() {
        return (
            <DialogModal isOpen={true} centered labelledBy="paypal-process-title">
                <ModalBody>
                    <div className="d-flex flex-column align-items-center">
                        <Spinner />
                        <div id="paypal-process-title" className="mt-3">
                            Processing your payment
                            {this.props.invoiceId ? ` for invoice ${this.props.invoiceId}` : ''}
                            ...
                        </div>
                    </div>
                </ModalBody>
            </DialogModal>
        );
    }

    pendingView(targetUrl: string) {
        return (
            <div>
                <h3>Payment Pending</h3>
                <Card className="mt-4">
                    <CardBody>
                        <p>
                            Your PayPal transaction is currently pending - this usually occurs if you are paying
                            directly from your bank account (an "eCheque") rather than credit card.
                        </p>
                        <p>
                            Any pending services will be automatically provisioned as soon as the payment has cleared -
                            please check{' '}
                            <ExternalLink href="https://www.paypal.com/" rel="noreferrer">
                                www.paypal.com
                            </ExternalLink>{' '}
                            to view the current status.
                        </p>
                        <Button color="primary" href={targetUrl}>
                            Continue
                        </Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    completeView() {
        const result = this.resultStore.item;
        if (!result) return null;
        const targetUrlBase = result.transaction_success_url ?? BillingInvoicesRoute;

        const url = new URL(targetUrlBase);
        url.searchParams.set('paypal_result', result.paypal_process_result);
        // Kept on cancelled too: a failed capture leaves the invoice open, so the order is still live.
        if (this.props.actionId) {
            url.searchParams.set('action_id', this.props.actionId);
        }
        const targetUrl = url.toString();
        switch (result.paypal_process_result) {
            case 'success': {
                // navigate component can't handle absolute urls, so we use location here.
                if (targetUrl.startsWith('http')) {
                    window.location.replace(targetUrl);
                }

                return (
                    <>
                        <DialogModal isOpen={true} centered labelledBy="paypal-success-title">
                            <ModalBody>
                                <div className="d-flex flex-column align-items-center">
                                    <Spinner />
                                    <div id="paypal-success-title" className="mt-3">
                                        Payment Successful! Redirecting...
                                    </div>
                                </div>
                            </ModalBody>
                        </DialogModal>
                        {!targetUrl.startsWith('http') && <Navigate to={targetUrl} replace />}
                    </>
                );
            }
            case 'pending':
                return this.pendingView(targetUrl);
            case 'cancelled':
                return <CancelBlurb invoiceId={this.props.invoiceId} returnUrl={targetUrl} />;
        }
    }

    render() {
        return (
            <div className="paypalProcess">
                <WaitFor
                    operation={this.resultStore}
                    loading={this.loadingView()}
                    errored={(e) => <TransactionError heading="Transaction Error" error={e} />}
                >
                    {() => this.completeView()}
                </WaitFor>
            </div>
        );
    }
}

export function PaypalProcess() {
    return (
        <WithSearch>
            {(search) => (
                <PaypalProcessWithParams
                    invoiceId={search.get('invoiceid')}
                    transactionId={search.get('transactionid')}
                    actionId={search.get('actionid')}
                    payerId={search.get('PayerID')}
                />
            )}
        </WithSearch>
    );
}
